import React from 'react'

import { Stack, Typography, Box } from '@mui/material'
import { BadgePlus } from 'lucide-react'

import { TermInfo } from 'components/UI/TermInfo'

/**
 * "You own nothing yet" — the certificates list with no rows.
 *
 * ## This is NOT the failure state, and the distinction is load-bearing
 *
 * A failed fetch leaves `certificates` undefined, which makes `isEmpty` true as
 * well — so an empty account and a broken connection are the SAME condition as
 * far as this component can see. The page therefore checks `isError` BEFORE
 * `isEmpty` (VERA-837) and routes failures to `CertificatesLoadError`, which
 * classifies them further: no HTTP response at all -> "We couldn't connect",
 * 401/403 -> sign in, otherwise -> a server error.
 *
 * That ordering is the only thing keeping this copy honest. Reached wrongly,
 * "No certificates yet" tells someone whose network dropped that they own
 * nothing — a factual claim about their property, made because a request timed
 * out. Do not reorder those branches.
 *
 * ## Why it stays passive
 *
 * There is deliberately no button. A consumer does not CREATE a certificate —
 * one arrives when a brand issues it and they claim it, usually by tapping the
 * tag on the item they just bought. The honest empty state names the condition
 * and says what will end it. A call to action here would have to invent an
 * action the user cannot take.
 */
export const EmptyCertificates = () => {
  return (
    <Stack
      sx={{
        px: 2,
        textAlign: 'center',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '276px',
      }}
    >
      {/*
        THEME TOKENS, not the hardcoded `#f2f2f2` / `#e6e6e6` / `#000` this used
        to carry. `apps/app` is multi-tenant — a brand sets its own palette in
        Studio — so a literal grey is a Vera grey rendered inside someone else's
        site, and a literal black ignores a brand that themed its text. The
        surrounding empty states (`ActivityPage`'s, the Provenance one) already
        read from the theme; this was the odd one out.
      */}
      <Box
        sx={theme => ({
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        })}
      >
        <BadgePlus size={24} color='currentColor' />
      </Box>
      <Typography
        sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}
      >
        No certificates yet
      </Typography>
      {/*
        Names what ENDS the state, not just that it exists. "Once you've become
        an owner" is passive and does not tell anyone what to do; tapping the tag
        on an item is the actual mechanism, and it is the one thing a person
        holding a Vera-tagged product can act on immediately.
      */}
      <Typography
        sx={{ color: 'text.secondary', maxWidth: 320, fontSize: '14px', lineHeight: 1.5 }}
      >
        Tap the tag on an item you own to claim its certificate. It will appear
        here once it is yours.
        {/*
          VRG-426 — the app's single `(i)` for "tag", and this is where it earns its
          place rather than in the scan sheet.

          This copy assumes the reader knows what "the tag" IS: it names the mechanism
          ("tap the tag") without ever saying that a tag is the physical NFC device on
          the item. That assumption is safe for everyone EXCEPT the reader of this
          screen — an account with no certificates is, by definition, someone who has
          not yet claimed anything, which is the one population that may never have met
          the word. Every other surface carrying "tag" is reached from a certificate the
          reader already owns.

          NOT `ScanTag`'s "Scan the tag" heading, which was the other candidate. Two
          reasons, the second decisive:
            - that sheet already carries a help control ("Where to scan?"), so a second
              affordance would compete with the one a mid-scan reader actually wants;
            - `ScanTag` opens FROM the Gift drawer, so a term sheet on top of it would be
              the THIRD stacked bottom sheet — the arrangement §52 forbids outright, and
              the arrangement `ScanTag`'s own "where to scan" step was rewritten to undo.
          Here the empty state is the page, so the term sheet is the first sheet.

          Trailing the sentence rather than beside the heading, because the heading is
          "No certificates yet" and does not contain the word. The cost is that a
          trailing glyph cannot say WHICH word it defines; `TermInfo`'s `aria-label`
          (`What does "Tag" mean?`) covers that for a screen reader, and sighted readers
          have one candidate term in two sentences. Accepted rather than splitting the
          sentence around an inline 22px control, which would disturb the leading of a
          centred, wrapped paragraph.
        */}
        <Box component='span' sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
          <TermInfo termId='tag' />
        </Box>
      </Typography>
    </Stack>
  )
}
